import { Alert } from '@mui/material';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import Tabs from '../../../../components/Tabs';
import { useLanguage } from '../../../../contexts/language';
import { VesselTemplateSubPageContext } from '../types';
import InsuranceTabContent from './InsuranceTabContent';
import useFreshDataEnsurer from '../../../../hooks/useFreshDataEnsurer';
import useDraftList, { ListElement } from '../../../../hooks/useDraftList';
import { VesselInsurance } from '../../../../types/insurance';
import { useNavigationGuardBlocker } from '../../../../hooks/useNavigationGuard';
import { useCurrentUser } from '../../../../contexts/currentUser';

const VesselInsurancesPage = () => {
  const { vessel, refetchVessel, insuranceManagement } =
    useOutletContext<VesselTemplateSubPageContext>();
  const { isOperator } = useCurrentUser();

  const {
    insurances,
    refetch: refecthInsurance,
    isFetching: isFetchingInsurance,
  } = insuranceManagement;
  const { t } = useLanguage();

  const areInsurancesFresh = useFreshDataEnsurer(
    isFetchingInsurance,
    refecthInsurance,
  );

  const { list, isReady, operations } = useDraftList(
    insurances,
    areInsurancesFresh,
  );

  const { nonExpiredInsurances, expiredInsurances } = useMemo(() => {
    const nonExpiredInsurances: ListElement<VesselInsurance>[] = [];
    const expiredInsurances: ListElement<VesselInsurance>[] = [];
    list.forEach(item => {
      if (item.isDraft || !item.data.isExpired) {
        nonExpiredInsurances.push(item);
      } else {
        expiredInsurances.push(item);
      }
    });
    return { nonExpiredInsurances, expiredInsurances };
  }, [list]);

  useNavigationGuardBlocker(() => Boolean(list.find(item => item.isDraft)));

  const canInsurancesBeAdded = !vessel.deleteDate && !isOperator;

  return (
    <Tabs
      tabs={[{ label: t('content.valid') }, { label: t('content.expired') }]}
      variant="buttons"
    >
      <InsuranceTabContent
        vessel={vessel}
        draftListManager={{ list: nonExpiredInsurances, isReady, operations }}
        areInsurancesPending={!areInsurancesFresh}
        isFetchingInsurances={isFetchingInsurance}
        onInsurancesDataUpdate={() => {
          refetchVessel();
          refecthInsurance();
        }}
        emptyContent={
          canInsurancesBeAdded ? (
            <Alert severity="error" className="mb-4 shadow-iots">
              {t('validation.valid-insurances-minimum-button')}
            </Alert>
          ) : (
            <div className="grid place-items-center w-full grow">
              <h1 className="text-2xl font-bold">
                {t(
                  isOperator
                    ? 'content.contact-admin-to-add-insurance'
                    : 'content.no-insurances-found',
                )}
              </h1>
            </div>
          )
        }
        showAddButton={canInsurancesBeAdded}
      />
      <InsuranceTabContent
        vessel={vessel}
        draftListManager={{ list: expiredInsurances, isReady, operations }}
        areInsurancesPending={!areInsurancesFresh}
        isFetchingInsurances={isFetchingInsurance}
        onInsurancesDataUpdate={() => {
          refetchVessel();
          refecthInsurance();
        }}
        emptyContent={
          <div className="grid place-items-center w-full grow">
            <h1 className="text-2xl font-bold">
              {t(
                isOperator
                  ? 'content.contact-admin-to-add-insurance'
                  : 'content.no-insurances-found',
              )}
            </h1>
          </div>
        }
      />
    </Tabs>
  );
};

export default VesselInsurancesPage;
